import { memo, useState, useCallback, useEffect, useRef } from 'react'
import {
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material'
import ManagementSearchBar from '../shared/ManagementSearchBar'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'

const FILTER_DEBOUNCE_MS = 300

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Current' },
  { value: 'scheduled', label: 'Upcoming' },
  { value: 'expired', label: 'Expired' },
]

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'tp', label: 'Fixed (TP)' },
  { value: 'discount', label: 'Discount' },
  { value: 'percentage', label: 'Percentage' },
]

function PromoCatalogFilters({ onFiltersChange }) {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [type, setType] = useState('')
  const [validFrom, setValidFrom] = useState('')
  const [validTo, setValidTo] = useState('')
  const mountedRef = useRef(false)

  const debouncedValidFrom = useDebouncedValue(validFrom, FILTER_DEBOUNCE_MS)
  const debouncedValidTo = useDebouncedValue(validTo, FILTER_DEBOUNCE_MS)

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    onFiltersChange({
      search,
      status,
      type,
      validFrom: debouncedValidFrom || undefined,
      validTo: debouncedValidTo || undefined,
    })
  }, [debouncedValidFrom, debouncedValidTo, search, status, type, onFiltersChange])

  const handleSearchApply = useCallback(
    (value) => {
      setSearch(value)
      onFiltersChange({
        search: value,
        status,
        type,
        validFrom: debouncedValidFrom || undefined,
        validTo: debouncedValidTo || undefined,
      })
    },
    [status, type, debouncedValidFrom, debouncedValidTo, onFiltersChange],
  )

  const handleStatusChange = useCallback(
    (event) => {
      const value = event.target.value
      setStatus(value)
      onFiltersChange({
        search,
        status: value,
        type,
        validFrom: debouncedValidFrom || undefined,
        validTo: debouncedValidTo || undefined,
      })
    },
    [search, type, debouncedValidFrom, debouncedValidTo, onFiltersChange],
  )

  const handleTypeChange = useCallback(
    (event) => {
      const value = event.target.value
      setType(value)
      onFiltersChange({
        search,
        status,
        type: value,
        validFrom: debouncedValidFrom || undefined,
        validTo: debouncedValidTo || undefined,
      })
    },
    [search, status, debouncedValidFrom, debouncedValidTo, onFiltersChange],
  )

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems={{ md: 'center' }}
      sx={{ mb: 2 }}
      flexWrap="wrap"
    >
      <Box sx={{ flex: 1, minWidth: 240 }}>
        <ManagementSearchBar
          label="Search"
          placeholder="Search by Article Code or product name"
          onApplySearchQuery={handleSearchApply}
          mb={0}
        />
      </Box>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Status</InputLabel>
        <Select value={status} label="Status" onChange={handleStatusChange}>
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Type</InputLabel>
        <Select value={type} label="Type" onChange={handleTypeChange}>
          {TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        type="date"
        label="Valid from"
        value={validFrom}
        onChange={(e) => setValidFrom(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 150 }}
      />
      <TextField
        size="small"
        type="date"
        label="Valid to"
        value={validTo}
        onChange={(e) => setValidTo(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 150 }}
      />
    </Stack>
  )
}

export default memo(PromoCatalogFilters)
